<script>
import { mapGetters, mapActions } from "vuex";
import axios from "axios";
import EmptyState from "@/components/EmptyState.vue";
import TableSkeleton from "@/components/TableSkeleton.vue";
import DatePicker from "@/components/DatePicker.vue";
import { loadListView, saveListView } from "@/utils/applications-list-view";
import { BITRIX_SOURCES } from "@/service/bitrix-sources";

export default {
  components: { EmptyState, TableSkeleton, DatePicker },
  props: {
    value: Number,
    // Source toggle from the page header: ALL | ADMISSION | QABUL | LEAD.
    source: { type: String, default: "ALL" },
  },

  data: (vm) => ({
    // Position of the DTM tab in the store's statuses/filterStatuses/exportFilterStatuses
    // arrays. Kept as a named constant so the tab-reveal watcher below does not hide a bare
    // 2 that would silently point at the wrong tab if the strip is ever reordered.
    DTM_TAB_INDEX: 2,
    isLoading: false,
    canCreate: false,
    canUpdate: false,
    canDelete: false,
    canExport: false,

    search: null,
    selectedIds: [],
    selectAllLoading: false,
    selectAllToken: 0,
    selectAllMode: false,
    isResending: false,

    options: {},
    totalItemsCount: 0,
    itemsPerPage: 20,
    page: 1,

    isExport: false,
    isDeleteModal: false,
    deleteMessage: "",
    deleteAction: null,

    // Column visibility
    tableSettingsOpen: false,
    visibleColumnKeys: ["fullName", "registrationDate", "modifiedDate", "faculty", "mobilePhone", "passportNumber", "passedDate"],
    allColumns: [
      { key: "fullName", label: vm.$t("Full name") },
      { key: "registrationDate", label: vm.$t("Date") },
      { key: "modifiedDate", label: vm.$t("Last updated") },
      { key: "faculty", label: vm.$t("Education direction") },
      { key: "mobilePhone", label: vm.$t("Phone") },
      { key: "passportNumber", label: vm.$t("Passport") },
      { key: "passedDate", label: vm.$t("Test kuni") },
      { key: "dtmScore", label: vm.$t("DTM result") },
      { key: "dtmAbiturientNumber", label: vm.$t("Applicant dtm ID number") },
    ],

    // ── Create Student Dialog ──
    createDialog: false,
    createLoading: false,
    codeDialog: false,
    createdStudentInfo: null,

    // ── DTM xlsx bulk import (ADMIN_SUPER only) ──
    dtmImportDialog: false,
    dtmImportFile: null,
    dtmImportRunning: false,
    dtmImportStatus: null,
    dtmImportPollTimer: null,

    // ── Lead xlsx upload ──
    uploadLeadsDialog: false,
    uploadLeadsLoading: false,
    uploadLeadsFile: null,
    uploadLeadsResult: null,
    uploadLeadsSourceId: "CALL",
    bitrixSources: BITRIX_SOURCES,
    newStudent: {
      firstName: "",
      lastName: "",
      middleName: "",
      passportNumber: "",
      mobilePhone: "",
      birthDate: "",
      jshir: "",
      gender: null,
      educationType: { id: null },
      educationLanguage: { id: null },
      educationFaculty: { id: null },
      region: { id: null },
      city: { id: null },
      country: { id: 1 },
    },

    // ── Filter state ──
    showFilters: false,
    isFiltering: false,
    filters: {},

    // Filter menus
    regionMenu: false,
    districtMenu: false,
    educationFacultyMenu: false,
    educationTypeMenu: false,
    educationLanguageMenu: false,
    genderMenu: false,
    typeAbiturientMenu: false,

    // Filter search strings
    searchRegion: "",
    searchDistrict: "",
    searchEducationFaculty: "",
    searchEducationType: "",
    searchEducationLanguage: "",
    searchGender: "",
    searchTypeAbiturient: "",

    // Filter selections
    selectedRegion: [],
    selectedDistrict: [],
    selectedEducationFaculty: [],
    selectedEducationType: [],
    selectedEducationLanguage: [],
    selectedGender: [],
    selectedTypeAbiturient: [],

    // Contract created-date (contract2026.created_date) range filter, "yyyy-MM-dd" in Asia/Tashkent — so
    // the list lines up with the Kontrakt 2026-2027 list/export, not the registration date.
    // Empty = no date filter; from===to = a single day (Bugungi / Kechagi presets).
    selectedDateFrom: "",
    selectedDateTo: "",
    // The "Sana" chip is a toggle (like the other filter chips): collapsed by default, it reveals the
    // Bugungi/Kechagi presets + the date pickers inline. Kept open while a date filter is active.
    showDateControls: false,

    // Filter options (static)
    educationType: [
      { id: 1, name: vm.$t("Kunduzgi ta'lim") },
      { id: 2, name: vm.$t("Kechki ta'lim") },
      { id: 3, name: vm.$t("Sirtqi ta'lim") },
      { id: 4, name: vm.$t("Kunduzgi 3-smena") },
    ],
    educationLanguage: [
      { id: 1, name: vm.$t("O'zbek") },
      { id: 2, name: vm.$t("Rus") },
      { id: 3, name: vm.$t("Ingliz") },
    ],
    gender: [
      { id: 1, name: vm.$t("Erkak"), value: "MALE" },
      { id: 2, name: vm.$t("Ayol"), value: "FEMALE" },
    ],
    typeAbiturient: [
      { id: 0, name: vm.$t("Imtihon"), value: "ABITURIYENT" },
      { id: 1, name: vm.$t("Magistratura"), value: "MASTER" },
      { id: 2, name: "Perevod", value: "PEREVOD" },
      { id: 3, name: "2-mutaxassislik", value: "SECOND_SPES" },
    ],
    // Options for the "Talaba qo'shish" create form. Exam-takers must be ABITURIENT
    // (no Y) so they land on the qabul exam step; the filter list above keeps
    // ABITURIYENT for matching legacy rows.
    createTypeOptions: [
      { name: vm.$t("Abituriyent"), value: "ABITURIENT" },
      { name: vm.$t("Magistratura"), value: "MASTER" },
      { name: "Perevod", value: "PEREVOD" },
      { name: "2-mutaxassislik", value: "SECOND_SPES" },
    ],

    // Filter options (dynamic)
    region: [],
    district: [],
    educationFaculty: [],
    localFaculties: [],
    createDistricts: [],
  }),

  beforeDestroy() {
    // The import poll is a setInterval, so leaving the page without clearing it would keep hitting
    // the status endpoint for the rest of the session.
    this.stopDtmImportPolling();
  },
  created() {
    // Restore the list view (tab/page/search/filters) the user left on before
    // opening an application, so the back button returns here, not to page 1.
    const saved = loadListView();
    if (saved) {
      if (saved.page) this.page = saved.page;
      if (saved.itemsPerPage) this.itemsPerPage = saved.itemsPerPage;
      if (saved.search != null) this.search = saved.search;
      if (saved.isFiltering) {
        this.isFiltering = true;
        this.filters = saved.filters || {};
        // Re-hydrate the Sana date UI mirrors. The data query is already restored via
        // this.filters.dateFrom/To, but the chip highlight (dateFilterActive) and the
        // Bugungi/Kechagi active state (isDatePreset) read these separate fields — without
        // this they stay "" on refresh, so an active preset (e.g. Kechagi) looked unchosen.
        this.selectedDateFrom = this.filters.dateFrom || "";
        this.selectedDateTo = this.filters.dateTo || "";
      }
    }
    this.fetchRegions();
    this.fetchDistricts();
    this.loadFaculties();
  },

  async mounted() {
    this.canCreate = await this.hasRole("ARIZALAR", "CREATE");
    this.canUpdate = await this.hasRole("ARIZALAR", "UPDATE");
    this.canDelete = await this.hasRole("ARIZALAR", "DELETE");
    this.canExport = await this.hasRole("ARIZALAR", "EXCEL");
    // The watcher only fires on a change, so landing directly on the DTM tab (restored session
    // or a ?tab= link) would otherwise arrive with the DTM columns hidden.
    this.revealDtmColumns(this.tab);
  },

  computed: {
    ...mapGetters("admin-applications", ["GET_APPLICATIONS"]),
    items: (vm) => vm.GET_APPLICATIONS,

    ...mapGetters("auth", ["GET_USER"]),
    user: (vm) => vm.GET_USER,

    isSupperAdmin: (vm) => vm.user.role == "ADMIN_SUPER" || vm.user.role == "QABUL_QILUVCHI",
    /** Strictly ADMIN_SUPER — isSupperAdmin above also covers QABUL_QILUVCHI, which is too wide for
     *  an action that creates contracts and pushes them to Bitrix. Mirrors the server-side rule. */
    isStrictSuperAdmin: (vm) => vm.user.role === "ADMIN_SUPER",
    dtmImportDetail: (vm) => (vm.dtmImportStatus && vm.dtmImportStatus.detail) || {},
    dtmImportPercent: (vm) => {
      const s = vm.dtmImportStatus;
      if (!s || !s.total) return 0;
      return Math.round((s.processed / s.total) * 100);
    },

    tab: {
      get: (vm) => vm.value,
      set(v) {
        this.$emit("input", v);
      },
    },
    paginationCount() {
      return Math.ceil(this.totalItemsCount / this.itemsPerPage);
    },

    allSelected() {
      if (this.selectAllMode) return this.totalItemsCount > 0;
      return this.totalItemsCount > 0 && this.selectedIds.length >= this.totalItemsCount;
    },
    pageAllSelected() {
      if (this.selectAllMode) return this.items.length > 0;
      return this.items.length > 0 && this.items.every((item) => this.selectedIds.includes(item.id));
    },
    someSelected() {
      if (this.selectAllMode) return false;
      return this.selectedIds.length > 0 && !this.allSelected;
    },
    selectionCount() {
      return this.selectAllMode ? this.totalItemsCount : this.selectedIds.length;
    },

    visibleColumns() {
      const map = new Map(this.allColumns.map((c) => [c.key, c]));
      return this.visibleColumnKeys.map((k) => map.get(k)).filter(Boolean);
    },

    tableHeaders() {
      const base = [{ text: "", value: "checkbox", width: 50, sortable: false }];
      const dynamic = this.visibleColumns.map((c) => ({ text: c.label, value: c.key, sortable: false }));
      return [...base, ...dynamic];
    },

    // Filter computed
    filteredRegion() {
      const s = this.searchRegion.toLowerCase();
      return (this.region || []).filter((o) => o.name.toLowerCase().includes(s));
    },
    filteredDistrict() {
      const s = this.searchDistrict.toLowerCase();
      return (this.district || []).filter((o) => o.name.toLowerCase().includes(s));
    },
    filteredEducationFaculty() {
      const s = this.searchEducationFaculty.toLowerCase();
      return (this.educationFaculty || []).filter((o) => o.name.toLowerCase().includes(s));
    },
    filteredEducationType() {
      const s = this.searchEducationType.toLowerCase();
      return this.educationType.filter((o) => o.name.toLowerCase().includes(s));
    },
    filteredEducationLanguage() {
      const s = this.searchEducationLanguage.toLowerCase();
      return this.educationLanguage.filter((o) => o.name.toLowerCase().includes(s));
    },
    filteredGender() {
      const s = this.searchGender.toLowerCase();
      return this.gender.filter((o) => o.name.toLowerCase().includes(s));
    },
    filteredTypeAbiturient() {
      const s = this.searchTypeAbiturient.toLowerCase();
      return this.typeAbiturient.filter((o) => o.name.toLowerCase().includes(s));
    },

    // A non-ALL source uses the same server-side filter path as the panel filters.
    useFilterPath() {
      return this.isFiltering || (this.source && this.source !== "ALL");
    },
    // Panel filters merged with the source toggle for the POST filter body.
    effectiveFilters() {
      const merged = { ...(this.filters || {}) };
      // REFERRAL / CALL_MARKAZ are orthogonal to `source`, so they map to their own filter flags;
      // ADMISSION / QABUL / LEAD continue to go through `source`.
      if (this.source === "REFERRAL") merged.referralEmployeeOnly = true;
      else if (this.source === "CALL_MARKAZ") merged.callMarkazOnly = true;
      else if (this.source && this.source !== "ALL") merged.source = this.source;
      // Carry the search box into the filter body so it works alongside source/panel filters
      // (the GET path already searches via name=; the filtered POST path needs it here).
      if (this.search) merged.search = this.search;
      return merged;
    },
    // True when a Sana (contract-date) filter is set — keeps the controls revealed and highlights the chip.
    dateFilterActive() {
      return !!(this.selectedDateFrom || this.selectedDateTo);
    },
  },

  watch: {
    // Debounced live search (mirrors AdminContract2026List): typing re-fetches after a short pause
    // instead of only on Enter / magnifier click — otherwise the box looks broken on every tab.
    search() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => this.getItems(), 400);
    },

    options: {
      handler() {
        this.getItems();
      },
      deep: true,
    },

    tab(value) {
      this.page = 1;
      this.selectedIds = [];
      this.selectAllMode = false;
      this.revealDtmColumns(value);
      this.$nextTick(() => {
        this.getItems();
      });
    },

    source() {
      this.page = 1;
      this.selectedIds = [];
      this.selectAllMode = false;
      this.$nextTick(() => {
        this.getItems();
      });
    },

    page() {
      // selections persist across pages
    },

    isLoading(v) {
      this.$emit("loading", v);
    },

    "newStudent.region.id"(regionId) {
      this.newStudent.city.id = null;
      if (regionId) {
        this.loadCreateDistricts(regionId);
      } else {
        this.createDistricts = [];
      }
    },
  },

  methods: {
    /**
     * Show the DTM columns when the DTM tab is opened.
     *
     * That tab exists to display DTM data, but both columns are off by default and this list
     * does not persist column choices — so without this it opens with its own columns hidden
     * every single time, and staff would have to reopen the column picker on each visit.
     *
     * Leaving the tab deliberately does not hide them again: choosing to keep them visible
     * elsewhere is reasonable, and silently undoing a user's column choice is worse.
     */
    revealDtmColumns(tabIndex) {
      if (tabIndex !== this.DTM_TAB_INDEX) return;
      const missing = ["dtmScore", "dtmAbiturientNumber"].filter(
        (key) => !this.visibleColumnKeys.includes(key)
      );
      if (missing.length) {
        this.visibleColumnKeys = [...this.visibleColumnKeys, ...missing];
      }
    },

    ...mapActions("admin-applications", [
      "INIT_APPLICATIONS",
      "INIT_FILTERED_APPLICATIONS",
      "FETCH_VIEW_IDS",
      "INIT_BY_ID",
      "EXPORT_EXCEL",
      "EXPORT_EXCEL_BY_FILTER",
      "CREATE_APPLICATION",
      "UPLOAD_LEADS",
      "DELETE_APPLICATION",
      "DELETE_APPLICATIONS_BY_IDS",
      "BULK_ACCEPT_APPLICATIONS",
      "RESEND_BITRIX_BY_IDS",
    ]),

    getItems() {
      // Single-flight: at most one request in flight, so out-of-order responses can't overwrite each
      // other. The old code `return`ed here and did nothing else — silently DROPPING any search / tab /
      // page change made while a request was in flight (the box showed the new term but the list kept
      // the old result). That timing-dependent drop, plus isLoading only being cleared on success (a
      // transient error left it stuck true and killed every later search until reload), is the
      // "search sometimes not working" bug. Fix: record the query; always clear isLoading in finish();
      // and if the query changed while this request was in flight, run the latest one right after.
      if (this.isLoading) return;
      this.isLoading = true;
      this.options.status = this.tab;
      this.options.search = this.search;

      const querySignature = () => JSON.stringify([
        this.tab, this.search, this.options.page, this.options.itemsPerPage,
        this.isFiltering, this.effectiveFilters || null,
      ]);
      const fetchedSignature = querySignature();

      // Persist the current view so returning from /application-show restores
      // this tab/page/search instead of resetting to page 1.
      saveListView({
        tab: this.tab,
        page: this.options.page || this.page,
        itemsPerPage: this.options.itemsPerPage || this.itemsPerPage,
        search: this.search,
        isFiltering: this.isFiltering,
        filters: this.filters,
      });

      const finish = () => {
        this.isLoading = false;
        // The query changed while this request was in flight (a search/tab/page the guard dropped) —
        // run the latest one now instead of leaving a stale result on screen.
        if (querySignature() !== fetchedSignature) this.$nextTick(() => this.getItems());
      };

      let request;
      if (this.useFilterPath) {
        this.options.filters = this.effectiveFilters;
        request = this.INIT_FILTERED_APPLICATIONS(this.options);
      } else {
        request = this.INIT_APPLICATIONS(this.options);
      }
      request
        .then((totalItemsCount) => { this.totalItemsCount = totalItemsCount; })
        .catch(() => { /* keep the last results on screen; finish() still clears isLoading + re-runs if stale */ })
        .finally(finish);
    },

    onExport() {
      this.isExport = true;
      if (this.useFilterPath) {
        this.EXPORT_EXCEL_BY_FILTER({ status: this.tab, filters: this.effectiveFilters }).then(() => {
          this.isExport = false;
        });
      } else {
        this.EXPORT_EXCEL({ status: this.tab }).then(() => {
          this.isExport = false;
        });
      }
    },

    getName: (item) => item?.name,

    getFullName(item) {
      const lastName = item.lastName || "";
      const firstName = item.firstName || "";
      const middleName = item.middleName || "";
      return `${lastName} ${firstName} ${middleName}`;
    },

    getKurs(item) {
      return item.kurs ? `${item.kurs} kurs` : "";
    },

    getPassport: (item) => item?.passportNumber,

    async onOpen({ id }) {
      await this.INIT_BY_ID({ id });
      this.$router.push(`/application-show/${id}`);
    },

    onClear() {
      this.search = null;
      clearTimeout(this._searchTimer); // cancel the debounce the line above just scheduled — fetch now

      this.$nextTick(() => {
        this.getItems();
      });
    },

    shouldShowRow(item) {
      const hasFullName =
        (item.firstName && item.firstName.trim()) ||
        (item.lastName && item.lastName.trim()) ||
        (item.middleName && item.middleName.trim());

      return (
        hasFullName ||
        (item.passportNumber && item.passportNumber.trim()) ||
        (item.contractNumber && item.contractNumber.trim()) ||
        (item.groupCode && item.groupCode.trim()) ||
        (item.mobilePhone && item.mobilePhone.trim()) ||
        item.kurs
      );
    },

    toggleSelectAll() {
      // Flag-based: no fetch, instant header + every-row tick. The bulk
      // endpoint receives the actual id list at action time.
      if (this.selectAllMode || this.allSelected) {
        this.selectAllMode = false;
        this.selectedIds = [];
        this.selectAllToken++;
        return;
      }
      this.selectAllMode = true;
      this.selectedIds = [];
    },

    toggleSelect(id) {
      // First per-row toggle while in select-all mode collapses to explicit
      // ids minus the one being unchecked, so the user can still deselect a
      // single row without clearing everything.
      if (this.selectAllMode) {
        this.selectAllMode = false;
        this.selectedIds = this.items.map((it) => it.id).filter((iid) => iid !== id);
        return;
      }
      const idx = this.selectedIds.indexOf(id);
      if (idx >= 0) {
        this.selectedIds.splice(idx, 1);
      } else {
        this.selectedIds.push(id);
      }
    },

    async resolveSelectedIds() {
      if (!this.selectAllMode) return [...this.selectedIds];
      try {
        return await this.FETCH_VIEW_IDS({
          status: this.tab,
          isFiltering: this.useFilterPath,
          filters: this.effectiveFilters,
          search: this.search,
          size: this.totalItemsCount,
        });
      } catch (e) {
        return [];
      }
    },

    onBulkDelete() {
      if (!this.selectAllMode && !this.selectedIds.length) return;
      this.deleteMessage = `${this.selectionCount} ta arizani o'chirmoqchimisiz?`;
      this.deleteAction = async () => {
        const ids = await this.resolveSelectedIds();
        if (ids.length) {
          await this.DELETE_APPLICATIONS_BY_IDS(ids);
        }
        this.selectedIds = [];
        this.selectAllMode = false;
        this.getItems();
        this.$emit("reload");
      };
      this.isDeleteModal = true;
    },

    async onBulkResendBitrix() {
      if (!this.selectAllMode && !this.selectedIds.length) return;
      this.isResending = true;
      try {
        const ids = await this.resolveSelectedIds();
        if (ids.length) await this.RESEND_BITRIX_BY_IDS(ids);
        this.selectedIds = [];
        this.selectAllMode = false;
        this.getItems();
      } finally {
        this.isResending = false;
      }
    },

    onDeleteSingle(item) {
      this.deleteMessage = this.$t("Are you sure you want to delete this application?");
      this.deleteAction = async () => {
        await this.DELETE_APPLICATION({ id: item.id });
        this.selectedIds = this.selectedIds.filter((id) => id !== item.id);
        this.getItems();
        this.$emit("reload");
      };
      this.isDeleteModal = true;
    },

    async confirmDelete() {
      if (this.deleteAction) await this.deleteAction();
      this.isDeleteModal = false;
      this.deleteAction = null;
    },

    // ── Create Student ──
    openCreateDialog() {
      this.newStudent = {
        firstName: "",
        lastName: "",
        middleName: "",
        passportNumber: "",
        mobilePhone: "",
        birthDate: "",
        jshir: "",
        gender: null,
        // Stored value must be ABITURIENT (no Y) for exam-takers — that's what the
        // qabul exam flow recognises. (The filter list above uses ABITURIYENT for
        // legacy data; do NOT reuse it here.) Non-exam types skip the qabul exam.
        typeAbiturient: "ABITURIENT",
        educationType: { id: null },
        educationLanguage: { id: null },
        educationFaculty: { id: null },
        region: { id: null },
        city: { id: null },
        country: { id: 1 },
      };
      this.createDialog = true;
    },

    async onCreateStudent() {
      if (this.createLoading) return;
      this.createLoading = true;
      const payload = { ...this.newStudent };
      if (payload.birthDate && !payload.birthDate.includes('T')) {
        payload.birthDate = payload.birthDate + 'T00:00:00Z';
      }
      const result = await this.CREATE_APPLICATION(payload);
      this.createLoading = false;
      if (result != null) {
        this.createDialog = false;
        this.createdStudentInfo = result;
        this.codeDialog = true;
        this.getItems();
      }
    },

    copyLoginCode() {
      if (!this.createdStudentInfo) return;
      const text = `Telefon: ${this.createdStudentInfo.phone}\nPIN kod: ${this.createdStudentInfo.loginCode}`;
      navigator.clipboard.writeText(text).then(() => {
        this.$store.dispatch("SET_SNACKBAR", { text: "Nusxa olindi!", color: "success" });
      });
    },

    // ── DTM xlsx bulk import ──
    async openDtmImportDialog() {
      this.dtmImportFile = null;
      this.dtmImportStatus = null;
      this.dtmImportDialog = true;
      // An import outlives a closed dialog and a page reload, so reattach to one that is running.
      // A single poll only ever showed a frozen snapshot — if it IS running, keep polling.
      await this.pollDtmImportOnce();
      if (this.dtmImportStatus && this.dtmImportStatus.phase === "RUNNING") {
        this.startDtmImportPolling();
      }
    },
    closeDtmImportDialog() {
      this.stopDtmImportPolling();
      this.dtmImportDialog = false;
    },
    async startDtmImport() {
      if (!this.dtmImportFile || this.dtmImportRunning) return;
      this.dtmImportRunning = true;
      this.dtmImportStatus = null;
      try {
        const form = new FormData();
        form.append("file", this.dtmImportFile);
        // `.post`, not `$post`: the $ helper swallows errors and resolves to null, so the 409 branch
        // below could never run and a rejected upload would look like a started one. This mirrors
        // UPLOAD_LEADS, which uses plain .post for the same reason.
        //
        // No Content-Type header either. Setting "multipart/form-data" by hand drops the boundary that
        // the browser generates from the FormData, and the server then cannot parse the parts.
        await this.$axios.post("admin/application/dtm-bulk-import", form);
        this.startDtmImportPolling();
      } catch (e) {
        this.dtmImportRunning = false;
        const status = e?.response?.status;
        this.$store.dispatch("SET_SNACKBAR", {
          text: status === 409
            ? "Import allaqachon ishlayapti"
            : (e?.response?.data?.message || e?.message || "Import boshlanmadi"),
          color: "error",
        });
        // 409 means someone else's run is live — show that one rather than nothing.
        if (status === 409) this.startDtmImportPolling();
      }
    },
    startDtmImportPolling() {
      this.stopDtmImportPolling();
      this.dtmImportRunning = true;
      this.dtmImportPollTimer = setInterval(this.pollDtmImportOnce, 2000);
      this.pollDtmImportOnce();
    },
    stopDtmImportPolling() {
      if (this.dtmImportPollTimer) {
        clearInterval(this.dtmImportPollTimer);
        this.dtmImportPollTimer = null;
      }
    },
    async pollDtmImportOnce() {
      try {
        const data = await this.$axios.$get("admin/application/dtm-bulk-status");
        const s = data && data.dtmImport;
        if (!s) return;
        this.dtmImportStatus = s;
        if (s.phase !== "RUNNING") {
          this.stopDtmImportPolling();
          this.dtmImportRunning = false;
          // Only refresh the grid once the run is over — reloading on every tick would fight the
          // import for database connections while it is creating rows.
          if (s.phase === "DONE") this.getItems();
        }
      } catch (_) {
        // A dropped poll is not a failed import; the next tick recovers.
      }
    },

    // ── Bulk lead xlsx upload ──
    openUploadLeadsDialog() {
      this.uploadLeadsFile = null;
      this.uploadLeadsResult = null;
      this.uploadLeadsSourceId = "CALL";
      this.uploadLeadsDialog = true;
    },
    onLeadsFileSelected(file) {
      this.uploadLeadsFile = file || null;
    },
    async onUploadLeads() {
      if (!this.uploadLeadsFile || this.uploadLeadsLoading) return;
      this.uploadLeadsLoading = true;
      try {
        const res = await this.UPLOAD_LEADS({ file: this.uploadLeadsFile, sourceId: this.uploadLeadsSourceId });
        this.uploadLeadsResult = res;
        this.getItems();
      } catch (_) {
        // snackbar shown by action
      } finally {
        this.uploadLeadsLoading = false;
      }
    },

    // ── Filter methods ──
    async fetchRegions() {
      try {
        const data = await this.$axios.$get("admin/region?page=0&size=120&name=null&sortField=&sortOrder=asc");
        const regionList = (data?.content || []).map((region) => ({
          id: region.id,
          name: region.names?.uz || region.names?.en || region.names?.ru || "---",
        }));
        this.region = regionList;
      } catch (e) {
        console.error("Failed to load regions", e);
      }
    },

    async fetchDistricts() {
      try {
        const data = await this.$axios.$get("admin/city?page=0&size=200&name=null&sortField=&sortOrder=asc");
        const districtList = (data?.content || []).map((city) => ({
          id: city.id,
          name: city.names?.uz || city.names?.en || city.names?.ru || "---",
        }));
        this.district = districtList;
      } catch (e) {
        console.error("Failed to load districts", e);
      }
    },

    async loadCreateDistricts(regionId) {
      try {
        const data = await this.$axios.$get(`admin/city/all/${regionId}`);
        this.createDistricts = (data || []).map((city) => ({
          id: city.id,
          name: city.name || "---",
        }));
      } catch (e) {
        console.error("Failed to load districts for region", e);
        this.createDistricts = [];
      }
    },

    async fetchDistrictsByRegions(regionIds) {
      if (!regionIds || regionIds.length === 0) {
        this.fetchDistricts();
        return;
      }
      try {
        const allRequests = regionIds.map((regionId) => {
          return this.$axios.$get(`admin/city/all/${regionId}`)
            .then((data) => data || [])
            .catch(() => []);
        });
        const results = await Promise.all(allRequests);
        const merged = results.flat();
        const seen = new Set();
        const uniqueDistricts = merged
          .filter((city) => city?.id && !seen.has(city.id) && seen.add(city.id))
          .map((city) => ({
            id: city.id,
            name: city.name || "---",
          }));
        this.district = uniqueDistricts;
      } catch (e) {
        console.error("Failed to load districts by region", e);
      }
    },

    async loadFaculties() {
      try {
        const data = await this.$axios.$get("admin/education/faculty/all");
        const all = (data || []).map((f) => ({
          id: f.id,
          name: f.name || "---",
          schetType: f.schetType,
        }));
        this.educationFaculty = all;
        this.localFaculties = all.filter((f) => String(f.schetType).toUpperCase() === "LOCAL");
      } catch (e) {
        console.error("Failed to load faculties", e);
      }
    },

    toggleAllColumns() {
      if (this.visibleColumnKeys.length === this.allColumns.length) {
        this.visibleColumnKeys = [];
      } else {
        this.visibleColumnKeys = this.allColumns.map((c) => c.key);
      }
    },

    resetColumns() {
      this.visibleColumnKeys = this.allColumns.map((c) => c.key);
    },

    applyFilters() {
      this.isFiltering =
        this.selectedEducationFaculty.length > 0 ||
        this.selectedEducationType.length > 0 ||
        this.selectedEducationLanguage.length > 0 ||
        this.selectedRegion.length > 0 ||
        this.selectedDistrict.length > 0 ||
        this.selectedGender.length > 0 ||
        this.selectedTypeAbiturient.length > 0 ||
        !!this.selectedDateFrom ||
        !!this.selectedDateTo;

      // Update districts based on region selection
      const regionIds = this.selectedRegion;
      if (regionIds.length > 0) {
        this.fetchDistrictsByRegions(regionIds);
      } else {
        this.fetchDistricts();
      }

      const filters = {
        selectedEducationFaculties: this.selectedEducationFaculty.map(String),
        selectedEducationTypes: this.selectedEducationType.map(String),
        selectedEducationLanguages: this.selectedEducationLanguage.map(String),
        selectedRegions: this.selectedRegion.map(String),
        selectedDistricts: this.selectedDistrict.map(String),
        selectedGenders: this.selectedGender.map((id) => {
          const g = this.gender.find((x) => x.id === id);
          return g ? g.value : "";
        }),
        selectedTypeAbiturients: this.selectedTypeAbiturient.map((id) => {
          const t = this.typeAbiturient.find((x) => x.id === id);
          return t ? t.value : "";
        }),
        dateFrom: this.selectedDateFrom || null,
        dateTo: this.selectedDateTo || null,
      };

      this.filters = filters;
      this.page = 1;
      // Filter change invalidates any current selection because rows outside
      // the new filter would still be in selectedIds and leak into bulk
      // actions. Cancel any in-flight select-all fetch too.
      this.selectAllToken++;
      this.selectedIds = [];
      this.$nextTick(() => {
        this.getItems();
      });
    },

    // --- Date range filter helpers (contract2026.created_date, Tashkent-local) ---
    // "Today" as seen in Tashkent, "yyyy-MM-dd" (en-CA gives ISO order regardless of browser locale).
    tashkentToday() {
      return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Tashkent" });
    },
    // n days before Tashkent-today, computed in UTC so it never drifts across a DST/hour boundary.
    tashkentDayOffset(days) {
      const [y, m, d] = this.tashkentToday().split("-").map(Number);
      const dt = new Date(Date.UTC(y, m - 1, d));
      dt.setUTCDate(dt.getUTCDate() + days);
      return dt.toISOString().slice(0, 10);
    },
    setDatePreset(which) {
      const target = which === "yesterday" ? this.tashkentDayOffset(-1) : this.tashkentToday();
      this.selectedDateFrom = target;
      this.selectedDateTo = target;
      this.applyFilters();
    },
    clearDateFilter() {
      this.selectedDateFrom = "";
      this.selectedDateTo = "";
      this.applyFilters();
    },
    // Highlight the Bugungi/Kechagi preset when the from/to range is exactly that single day.
    isDatePreset(which) {
      const day = which === "yesterday" ? this.tashkentDayOffset(-1) : this.tashkentToday();
      return this.selectedDateFrom === day && this.selectedDateTo === day;
    },
    // DatePicker emits "yyyy-MM-dd" (or null on clear); mirror it into the filter and re-query.
    onDateFrom(v) {
      this.selectedDateFrom = v || "";
      this.applyFilters();
    },
    onDateTo(v) {
      this.selectedDateTo = v || "";
      this.applyFilters();
    },

    getFilterLabel(selected, options, defaultLabel) {
      if (!selected.length) return defaultLabel;
      const names = options
        .filter((f) => selected.includes(f.id))
        .map((f) => f.name);
      if (names.length === 1) return names[0];
      if (names.length > 1) return `${names[0]}, ..`;
      return defaultLabel;
    },

    async hasRole(panel, op) {
      try {
        const res = await axios.get(`/admin/panel-permissions/check`, {
          params: { appPanel: panel, op }
        });
        return res.data === true;
      } catch {
        return false;
      }
    },
  },
};
</script>

<template>
  <div>
    <!-- ── Toolbar: Search (left) + Circle Buttons (right) ── -->
    <div class="toolbar-row">
      <v-text-field
        :placeholder="$t('Izlash...')"
        append-icon="mdi-magnify"
        v-model="search"
        dense
        hide-details
        outlined
        clearable
        class="search-input"
        @click:append="getItems"
        @keyup.enter="getItems"
        @click:clear="onClear"
      />

      <div class="circle-actions">
      <!-- Result count badge (matches the contract list style) -->
      <v-chip class="badge-count" color="primary" small>
        {{ totalItemsCount || 0 }}
      </v-chip>

      <!-- Filter -->
      <button
        class="filter-btn"
        :class="{ 'filter-btn--dark': $vuetify.theme.dark }"
        @click="showFilters = !showFilters"
      >
        <img src="../assets/filter.svg" alt="Filter" :class="{ 'filter-icon-dark': $vuetify.theme.dark }">
      </button>

      <!-- Ustunlar (column visibility) -->
      <v-menu v-model="tableSettingsOpen" :close-on-content-click="false" offset-y left transition="scale-transition">
        <template v-slot:activator="{ on, attrs }">
          <button
            class="circle-btn circle-btn--columns"
            :class="{ 'circle-btn--columns-dark': $vuetify.theme.dark }"
            v-bind="attrs" v-on="on"
          >
            <img src="/icons/column-list-icon.svg" alt="Columns" class="columns-icon" :class="{ 'columns-icon--dark': $vuetify.theme.dark }">
          </button>
        </template>
        <v-card class="columns-card" :style="{ backgroundColor: $vuetify.theme.dark ? '#1a1b23' : '#FFF' }">
          <div class="columns-header">
            <span class="columns-title">{{ $t("Ustunlar") }}</span>
            <v-btn text x-small color="#667085" @click="resetColumns">{{ $t("Reset") }}</v-btn>
          </div>
          <v-divider />
          <div class="columns-body">
            <div
              v-for="col in allColumns"
              :key="col.key"
              class="columns-item"
              @click="
                visibleColumnKeys.includes(col.key)
                  ? visibleColumnKeys.splice(visibleColumnKeys.indexOf(col.key), 1)
                  : visibleColumnKeys.push(col.key)
              "
            >
              <div class="cbox" :class="{ 'cbox--checked': visibleColumnKeys.includes(col.key) }">
                <svg v-if="visibleColumnKeys.includes(col.key)" viewBox="0 0 16 16" class="cbox__icon"><path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="#FFF" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
              </div>
              <span class="columns-label">{{ col.label }}</span>
            </div>
          </div>
        </v-card>
      </v-menu>

      <!-- Export -->
      <v-btn
        v-if="canExport"
        class="modern-export"
        :loading="isExport"
        :disabled="isExport || excelExportDisabled"
        @click="onExport"
      >
        <img src="/icons/excel-icon.svg" alt="Excel" class="export-icon">
        EXPORT
      </v-btn>

      <!-- Re-send selected applications to Bitrix -->
      <v-btn
        v-if="selectAllMode || selectedIds.length"
        class="modern-resend"
        :loading="isResending"
        @click="onBulkResendBitrix"
      >
        <img src="/icons/bitrix-icon.svg" alt="Bitrix" class="resend-icon">
        {{ $t("Qayta jo'natish") }}
      </v-btn>

      <!-- Add -->
      <button
        v-if="canCreate"
        class="circle-btn circle-btn--add"
        @click="openCreateDialog"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1500" stroke-width="2.5" stroke-linecap="round">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
      </button>

      <!-- Bulk upload leads from xlsx -->
      <button
        v-if="canCreate"
        class="square-btn square-btn--upload"
        :title="$t('Bazani yuklash')"
        @click="openUploadLeadsDialog"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1500" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="17 8 12 3 7 8"/>
          <line x1="12" y1="3" x2="12" y2="15"/>
        </svg>
      </button>

      <!-- DTM xlsx bulk import — ADMIN_SUPER only. Strictly the role, not `isSupperAdmin`, which
           also covers QABUL_QILUVCHI: this creates applications AND contracts and recommends them
           straight into Bitrix. -->
      <button
        v-if="isStrictSuperAdmin"
        class="square-btn square-btn--dtm"
        :title="$t('DTM import')"
        @click="openDtmImportDialog"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="9" y1="13" x2="15" y2="19"/><line x1="15" y1="13" x2="9" y2="19"/>
        </svg>
      </button>

      <!-- Edit -->
      <button
        v-if="selectedIds.length === 1 && canUpdate"
        class="circle-btn circle-btn--edit"
        @click="onOpen({ id: selectedIds[0] })"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
      </button>

      <!-- Delete -->
      <button
        v-if="(selectAllMode || selectedIds.length) && canDelete"
        class="circle-btn circle-btn--delete"
        @click="onBulkDelete"
      >
        <v-icon size="22" color="#fff">mdi-trash-can</v-icon>
        <span class="delete-badge">{{ selectionCount }}</span>
      </button>
      </div>
    </div>

    <!-- ── Create Student Dialog ── -->
    <v-dialog v-model="createDialog" max-width="680" persistent scrollable content-class="create-dialog-wrap">
      <v-card class="create-card" ref="createCard">
        <div class="create-header">
          <div class="create-header-left">
            <div class="create-header-icon">
              <svg class="create-header-svg" viewBox="0 0 512 512" width="24" height="24" fill="#1a1500" aria-hidden="true">
                <g><path d="m123.041 319.232c-4.957 5.966-7.168 13.845-5.881 21.641l19.922 120.663c2.424 14.68 16.294 24.675 31.036 22.24 14.711-2.429 24.668-16.324 22.24-31.036l-17.453-105.707 32.263-27.801z"/><path d="m243.423 319.232 32.263 27.801-17.453 105.707c-2.707 16.398 9.93 31.4 26.671 31.4 12.975-.001 24.422-9.383 26.605-22.605l19.922-120.663c1.287-7.796-.924-15.675-5.881-21.641h-82.127z"/><ellipse cx="224.275" cy="50.03" rx="50.03" ry="50.03" transform="matrix(.987 -.16 .16 .987 -5.118 36.571)"/><path d="m504.913 229.965h-80.514v36.072h-19.401v-36.072h-26.191v36.072c-12.06 0-22.737 0-34.783 0 13.976-2.984 22.19-17.283 18.022-30.74-2.649-8.551-27.175-87.74-31.305-101.073-7.124-23.002-28.09-38.457-52.17-38.457h-12.105c-11.103 10.25-25.926 16.527-42.192 16.527s-31.089-6.277-42.192-16.527h-11.651c-24.08 0-45.045 15.454-52.17 38.457-2.733 8.823-27.131 87.597-31.304 101.073-4.186 13.515 4.105 27.769 18.022 30.74-19 0-71.771 0-86.529 0-6.276 0-11.364 5.088-11.364 11.364v17.765c0 6.276 5.088 11.364 11.364 11.364h21.834v190.179c0 8.445 6.846 15.292 15.292 15.292s15.292-6.846 15.292-15.292v-190.179h363.777v190.179c0 8.445 6.846 15.292 15.292 15.292 8.445 0 15.292-6.846 15.292-15.292v-190.179h21.834c6.276 0 11.364-5.088 11.364-11.364v-17.765c0-6.276-5.088-11.364-11.364-11.364h17.85zm-255.743-11.666c-6.979 0-13.263 2.966-17.671 7.701 2.56 4.921 4.015 10.508 4.015 16.439s-1.455 11.518-4.015 16.439c3.305 3.551 7.666 6.104 12.584 7.16h-39.163c10.889-2.336 19.053-12.012 19.053-23.598 0-13.332-10.808-24.14-24.14-24.14h-57.069c4.495-14.512 9.031-29.158 13.558-43.774v32.233h24.318l-13.619-31.02c-2.263-5.156.081-11.17 5.236-13.433 5.158-2.263 11.169.081 13.433 5.236l17.281 39.362c8.327.726 15.831 4.311 21.531 9.769 6.408-6.137 15.094-9.915 24.668-9.915h43.058v-33.696c4.243 13.699 9.814 31.687 14.011 45.237z"/><path d="m406.938 178.68h80.514v36.072h-80.514z"/><path d="m387.537 214.752v-36.072h-26.191v35.961.111h.141z"/><path d="m368.136 127.395h77.681v36.071h-77.681z"/><path d="m465.219 127.395h29.024v36.071h-29.024z"/></g>
              </svg>
            </div>
            <div>
              <div class="create-title">{{ $t("Talaba qo'shish") }}</div>
              <div class="create-subtitle">{{ $t("Yangi talaba ma'lumotlarini kiriting") }}</div>
            </div>
          </div>
          <button class="create-close-btn" @click="createDialog = false" :disabled="createLoading">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>

        <v-card-text class="create-body" style="max-height: 68vh; overflow-y: auto;">
          <!-- Section: Personal -->
          <div class="create-section">
            <div class="create-section-head">
              <v-icon size="16" class="create-section-icon">mdi-account-outline</v-icon>
              <span class="create-section-label">{{ $t("Shaxsiy ma'lumotlar") }}</span>
            </div>
            <div class="create-grid create-grid--3">
              <v-text-field v-model="newStudent.lastName" :label="$t('Last name')" dense outlined hide-details class="create-field" />
              <v-text-field v-model="newStudent.firstName" :label="$t('First name')" dense outlined hide-details class="create-field" />
              <v-text-field v-model="newStudent.middleName" :label="$t('Middle name')" dense outlined hide-details class="create-field" />
            </div>
            <div class="create-grid create-grid--3">
              <v-text-field v-model="newStudent.passportNumber" :label="$t('Passport series and number')" dense outlined hide-details class="create-field" />
              <v-text-field v-model="newStudent.jshir" :label="$t('jshir')" dense outlined hide-details class="create-field" />
              <v-text-field v-model="newStudent.mobilePhone" :label="$t('Personal phone number')" placeholder="998901234567" dense outlined hide-details class="create-field" />
            </div>
            <div class="create-grid create-grid--3">
              <DatePicker
                :value="newStudent.birthDate"
                @input="newStudent.birthDate = $event"
                :label="$t('Date of birth')"
              />
              <v-select
                v-model="newStudent.gender"
                :items="[{ text: $t('Erkak'), value: 'MALE' }, { text: $t('Ayol'), value: 'FEMALE' }]"
                :label="$t('Jinsi')"
                :menu-props="{ offsetY: true, bottom: true, maxHeight: 200 }"
                dense outlined hide-details class="create-field"
              />
              <div></div>
            </div>
          </div>

          <!-- Section: Education -->
          <div class="create-section">
            <div class="create-section-head">
              <v-icon size="16" class="create-section-icon">mdi-school-outline</v-icon>
              <span class="create-section-label">{{ $t("Ta'lim ma'lumotlari") }}</span>
            </div>
            <div class="create-grid create-grid--3">
              <v-select
                v-model="newStudent.typeAbiturient"
                :items="createTypeOptions"
                item-text="name" item-value="value"
                :label="$t('Ariza turi')"
                :menu-props="{ offsetY: true, bottom: true, maxHeight: 200 }"
                dense outlined hide-details class="create-field"
              />
              <v-select
                v-model="newStudent.educationType.id"
                :items="educationType"
                item-text="name" item-value="id"
                :label="$t('Type of education')"
                :menu-props="{ offsetY: true, bottom: true, maxHeight: 200 }"
                dense outlined hide-details class="create-field"
              />
              <v-select
                v-model="newStudent.educationLanguage.id"
                :items="educationLanguage"
                item-text="name" item-value="id"
                :label="$t('Language of instruction')"
                :menu-props="{ offsetY: true, bottom: true, maxHeight: 200 }"
                dense outlined hide-details class="create-field"
              />
              <v-autocomplete
                v-model="newStudent.educationFaculty.id"
                :items="localFaculties"
                item-text="name" item-value="id"
                :label="$t('Field of study')"
                :menu-props="{ offsetY: true, bottom: true, maxHeight: 250 }"
                dense outlined hide-details class="create-field"
                :no-data-text="$t('No Data')"
              />
            </div>
          </div>

          <!-- Section: Address -->
          <div class="create-section create-section--last">
            <div class="create-section-head">
              <v-icon size="16" class="create-section-icon">mdi-map-marker-outline</v-icon>
              <span class="create-section-label">{{ $t("Manzil") }}</span>
            </div>
            <div class="create-grid create-grid--2">
              <v-autocomplete
                v-model="newStudent.region.id"
                :items="region"
                item-text="name" item-value="id"
                :label="$t('Region')"
                :menu-props="{ offsetY: true, bottom: true, maxHeight: 250 }"
                dense outlined hide-details class="create-field"
                :no-data-text="$t('No Data')"
              />
              <v-autocomplete
                v-model="newStudent.city.id"
                :items="createDistricts"
                item-text="name" item-value="id"
                :label="$t('City / District')"
                :menu-props="{ offsetY: true, bottom: true, maxHeight: 250 }"
                dense outlined hide-details class="create-field"
                :disabled="!newStudent.region.id"
                :no-data-text="$t('No Data')"
              />
            </div>
          </div>
        </v-card-text>

        <div class="create-footer">
          <v-btn class="create-cancel-btn" text @click="createDialog = false" :disabled="createLoading">
            {{ $t("Close") }}
          </v-btn>
          <v-btn class="create-save-btn" :loading="createLoading" @click="onCreateStudent" elevation="0">
            <v-icon left small>mdi-check</v-icon>
            {{ $t("Save") }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ── Login Code Dialog ── -->
    <v-dialog v-model="codeDialog" max-width="420" persistent>
      <v-card class="pa-6 text-center" rounded="lg">
        <v-icon size="56" color="success" class="mb-3">mdi-check-circle</v-icon>
        <h3 class="mb-1" style="font-size: 18px; font-weight: 600;">Talaba muvaffaqiyatli yaratildi</h3>
        <p class="grey--text text--darken-1 mb-4" style="font-size: 14px;">Quyidagi ma'lumotlar bilan tizimga kirish mumkin</p>

        <div style="background: #f5f5f5; border-radius: 12px; padding: 20px; margin-bottom: 16px;">
          <div class="mb-3">
            <div class="grey--text text--darken-1" style="font-size: 12px; text-transform: uppercase; letter-spacing: 1px;">Telefon raqam</div>
            <div style="font-size: 18px; font-weight: 600;" class="mt-1">{{ createdStudentInfo && createdStudentInfo.phone }}</div>
          </div>
          <v-divider class="mb-3"></v-divider>
          <div>
            <div class="grey--text text--darken-1" style="font-size: 12px; text-transform: uppercase; letter-spacing: 1px;">PIN kod (parol)</div>
            <div style="font-size: 32px; font-weight: 700; letter-spacing: 8px; font-family: monospace;" class="mt-1">{{ createdStudentInfo && createdStudentInfo.loginCode }}</div>
          </div>
        </div>

        <v-btn
            color="#ffd451"
            depressed
            rounded
            class="mb-2"
            style="font-weight: 600; width: 100%;"
            @click="copyLoginCode"
        >
          <v-icon left small>mdi-content-copy</v-icon>
          Nusxa olish
        </v-btn>

        <v-btn text rounded style="width: 100%;" @click="codeDialog = false">
          Yopish
        </v-btn>
      </v-card>
    </v-dialog>

    <!-- ── DTM xlsx bulk import ── -->
    <v-dialog v-model="dtmImportDialog" max-width="560" persistent>
      <v-card class="pa-6" rounded="lg">
        <div class="d-flex align-center mb-4">
          <v-icon size="28" color="#217346" class="mr-2">mdi-microsoft-excel</v-icon>
          <h3 style="font-size: 18px; font-weight: 600;">{{ $t("DTM import") }}</h3>
          <v-spacer />
          <v-btn icon :disabled="dtmImportRunning" @click="closeDtmImportDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <p class="grey--text text--darken-1 mb-4" style="font-size: 13px; line-height: 1.5;">
          {{ $t("Har bir qator uchun ariza va shartnoma yaratiladi, \"Tavsiya qilingan\" qilib belgilanadi va Bitrixga yuboriladi. PINFL yoki telefon takrorlansa qator o'tkazib yuboriladi. SMS yuborilmaydi.") }}
        </p>
        <!-- The status lives in memory, so a restart mid-run loses the progress display but not the
             imported students. Re-uploading is the recovery, and it is safe — say so, otherwise an
             interrupted run looks finished. -->
        <p class="grey--text text--darken-1 mb-4" style="font-size: 12.5px; line-height: 1.5;">
          {{ $t("Yurish uzilib qolsa (server qayta ishga tushsa), xuddi shu faylni qayta yuklang. Allaqachon qo'shilganlar dublikat sifatida o'tkazib yuboriladi, qolganlari qo'shiladi.") }}
        </p>

        <v-file-input
          v-model="dtmImportFile"
          accept=".xlsx"
          :label="$t('Excel fayl (.xlsx)')"
          :disabled="dtmImportRunning"
          prepend-icon="mdi-paperclip"
          outlined
          dense
          hide-details
          class="mb-4"
        />

        <!-- Live progress, polled from /dtm-bulk-status -->
        <div v-if="dtmImportStatus" class="dtm-progress mb-4">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="dtm-phase">{{ dtmImportStatus.phase }}</span>
            <span class="grey--text">{{ dtmImportStatus.processed }} / {{ dtmImportStatus.total }}</span>
          </div>
          <v-progress-linear
            :value="dtmImportPercent"
            :indeterminate="dtmImportRunning && !dtmImportStatus.total"
            color="#217346"
            height="8"
            rounded
          />
          <div class="dtm-counters mt-2">
            <span class="dtm-chip dtm-chip--ok">{{ $t('Yaratildi') }}: {{ dtmImportDetail.created || 0 }}</span>
            <span class="dtm-chip">{{ $t('Dublikat') }}: {{ dtmImportDetail.duplicates || 0 }}</span>
            <span class="dtm-chip dtm-chip--err">{{ $t('Xato') }}: {{ dtmImportDetail.failed || 0 }}</span>
          </div>
          <v-alert v-if="dtmImportStatus.lastError" type="error" dense outlined class="mt-3 mb-0">
            {{ dtmImportStatus.lastError }}
          </v-alert>
          <div v-if="(dtmImportDetail.errors || []).length" class="dtm-errors mt-3">
            <div v-for="(e, i) in dtmImportDetail.errors" :key="i" class="dtm-error-row">
              <!-- No whitespace before the colon: the line breaks that used to separate these
                   elements collapse to a space, which would render "Qator 5 : xato". -->
              <b>{{ $t('Qator') }} {{ e.row }}</b><span v-if="e.pinfl"> · {{ e.pinfl }}</span>: {{ e.error }}
            </div>
          </div>

          <!-- Names that did not match the catalog exactly. These rows WERE imported; the list is here
               so a wrong interpretation can be spotted, since it decides the contract's price. -->
          <div v-if="(dtmImportDetail.resolved || []).length" class="dtm-errors dtm-resolved mt-3">
            <div class="dtm-resolved-title">{{ $t('Nomi taxminan moslashtirildi, tekshiring') }}</div>
            <div v-for="(r, i) in dtmImportDetail.resolved" :key="'r' + i" class="dtm-error-row">{{ r }}</div>
          </div>
        </div>

        <v-btn
          block
          :loading="dtmImportRunning"
          :disabled="!dtmImportFile || dtmImportRunning"
          class="dtm-start-btn"
          @click="startDtmImport"
        >
          {{ $t('Yuklashni boshlash') }}
        </v-btn>
      </v-card>
    </v-dialog>

    <!-- ── Bulk lead upload dialog ── -->
    <v-dialog v-model="uploadLeadsDialog" max-width="520" persistent>
      <v-card class="pa-6" rounded="lg">
        <div class="d-flex align-center mb-4">
          <v-icon size="28" color="#ffb700" class="mr-2">mdi-file-upload-outline</v-icon>
          <h3 style="font-size: 18px; font-weight: 600;">{{ $t("Bazani yuklash") }}</h3>
          <v-spacer />
          <v-btn icon :disabled="uploadLeadsLoading" @click="uploadLeadsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <p class="grey--text text--darken-1 mb-4" style="font-size: 13px; line-height: 1.5;">
          {{ $t("Baza yuklash shablonidan foydalaning. Har bir qator yangi lead sifatida saqlanadi.") }}
        </p>

        <v-select
          v-model="uploadLeadsSourceId"
          :items="bitrixSources"
          item-text="label"
          item-value="value"
          :label="$t('Source')"
          :disabled="uploadLeadsLoading"
          prepend-icon="mdi-source-branch"
          outlined
          dense
          hide-details
          class="mb-4"
          :menu-props="{ bottom: true, offsetY: true, maxHeight: 240, contentClass: 'upload-source-menu' }"
        />

        <v-file-input
          v-model="uploadLeadsFile"
          accept=".xlsx"
          :label="$t('Excel fayl (.xlsx)')"
          :disabled="uploadLeadsLoading"
          prepend-icon="mdi-file-excel-outline"
          outlined
          dense
          show-size
          @change="onLeadsFileSelected"
        />

        <div v-if="uploadLeadsResult" class="mt-2 mb-2" style="background: #f5f5f5; border-radius: 10px; padding: 14px;">
          <div style="font-size: 13px;"><strong>{{ $t("Yaratildi") }}:</strong> {{ uploadLeadsResult.created }}</div>
          <div style="font-size: 13px;"><strong>{{ $t("Takroriy") }}:</strong> {{ uploadLeadsResult.duplicates }}</div>
          <div v-if="uploadLeadsResult.resynced" style="font-size: 13px; color: #2e7d32;"><strong>{{ $t("Qayta yuborildi") }}:</strong> {{ uploadLeadsResult.resynced }}</div>
          <div v-if="uploadLeadsResult.errors && uploadLeadsResult.errors.length" style="font-size: 13px; color: #c62828;">
            <strong>{{ $t("Xatoliklar") }}:</strong> {{ uploadLeadsResult.errors.length }}
          </div>
        </div>

        <div class="d-flex justify-end mt-3">
          <v-btn text :disabled="uploadLeadsLoading" @click="uploadLeadsDialog = false">
            {{ $t("Yopish") }}
          </v-btn>
          <v-btn
            color="#ffd451"
            depressed
            class="ml-2"
            style="font-weight: 600;"
            :loading="uploadLeadsLoading"
            :disabled="!uploadLeadsFile || uploadLeadsLoading"
            @click="onUploadLeads"
          >
            <v-icon left small>mdi-upload</v-icon>
            {{ $t("Yuklash") }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ── Filter Bar ── -->
    <v-fade-transition>
      <div v-if="showFilters" class="filter-bar">
        <div class="filter-row">
          <!-- Region -->
          <v-menu v-model="regionMenu" :close-on-content-click="false" offset-y transition="scale-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="button-primary" v-bind="attrs" v-on="on">
                {{ getFilterLabel(selectedRegion, region, $t('Viloyat')) }}
              </v-btn>
            </template>
            <v-card class="filter-card" :style="{ backgroundColor: $vuetify.theme.dark ? '#1a1b23' : '#FFF' }">
              <v-card-title>
                <v-text-field v-model="searchRegion" :label="$t('Qidiruv')" clearable hide-details dense />
              </v-card-title>
              <v-divider />
              <v-card-text class="filter-card-body">
                <div class="filter-checks">
                  <v-checkbox
                    v-for="item in filteredRegion" :key="item.id"
                    :label="item.name" :value="item.id"
                    v-model="selectedRegion" hide-details dense
                    class="mr-4 mb-2" style="min-width: 120px;"
                    @change="applyFilters"
                  />
                </div>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn text @click="regionMenu = false">{{ $t("Close") }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>

          <!-- District -->
          <v-menu v-model="districtMenu" :close-on-content-click="false" offset-y transition="scale-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="button-primary" v-bind="attrs" v-on="on">
                {{ getFilterLabel(selectedDistrict, district, $t('Tuman')) }}
              </v-btn>
            </template>
            <v-card class="filter-card" :style="{ backgroundColor: $vuetify.theme.dark ? '#1a1b23' : '#FFF' }">
              <v-card-title>
                <v-text-field v-model="searchDistrict" :label="$t('Qidiruv')" clearable hide-details dense />
              </v-card-title>
              <v-divider />
              <v-card-text class="filter-card-body">
                <div class="filter-checks">
                  <v-checkbox
                    v-for="item in filteredDistrict" :key="item.id"
                    :label="item.name" :value="item.id"
                    v-model="selectedDistrict" hide-details dense
                    class="mr-4 mb-2" style="min-width: 120px;"
                    @change="applyFilters"
                  />
                </div>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn text @click="districtMenu = false">{{ $t("Close") }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>

          <!-- Faculty -->
          <v-menu v-model="educationFacultyMenu" :close-on-content-click="false" offset-y transition="scale-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="button-primary" v-bind="attrs" v-on="on" style="max-width: 274px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                {{ getFilterLabel(selectedEducationFaculty, educationFaculty, $t("TA'LIM YO'NALISHI")) }}
              </v-btn>
            </template>
            <v-card class="filter-card" :style="{ backgroundColor: $vuetify.theme.dark ? '#1a1b23' : '#FFF' }">
              <v-card-title>
                <v-text-field v-model="searchEducationFaculty" :label="$t('Qidiruv')" clearable hide-details dense />
              </v-card-title>
              <v-divider />
              <v-card-text class="filter-card-body">
                <div class="filter-checks">
                  <v-checkbox
                    v-for="item in filteredEducationFaculty" :key="item.id"
                    :label="item.name" :value="item.id"
                    v-model="selectedEducationFaculty" hide-details dense
                    class="mr-4 mb-2" style="min-width: 120px;"
                    @change="applyFilters"
                  />
                </div>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn text @click="educationFacultyMenu = false">{{ $t("Close") }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>

          <!-- Education Type -->
          <v-menu v-model="educationTypeMenu" :close-on-content-click="false" offset-y transition="scale-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="button-primary" v-bind="attrs" v-on="on">
                {{ getFilterLabel(selectedEducationType, educationType, $t("TA'LIM SHAKLI")) }}
              </v-btn>
            </template>
            <v-card class="filter-card" :style="{ backgroundColor: $vuetify.theme.dark ? '#1a1b23' : '#FFF' }">
              <v-card-title>
                <v-text-field v-model="searchEducationType" :label="$t('Qidiruv')" clearable hide-details dense />
              </v-card-title>
              <v-divider />
              <v-card-text class="filter-card-body">
                <div class="filter-checks">
                  <v-checkbox
                    v-for="item in filteredEducationType" :key="item.id"
                    :label="item.name" :value="item.id"
                    v-model="selectedEducationType" hide-details dense
                    class="mr-4 mb-2" style="min-width: 120px;"
                    @change="applyFilters"
                  />
                </div>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn text @click="educationTypeMenu = false">{{ $t("Close") }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
        </div>

        <div class="filter-row" style="margin-top: 12px;">
          <!-- Education Language -->
          <v-menu v-model="educationLanguageMenu" :close-on-content-click="false" offset-y transition="scale-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="button-primary" v-bind="attrs" v-on="on">
                {{ getFilterLabel(selectedEducationLanguage, educationLanguage, $t("TA'LIM TILI")) }}
              </v-btn>
            </template>
            <v-card class="filter-card" :style="{ backgroundColor: $vuetify.theme.dark ? '#1a1b23' : '#FFF' }">
              <v-card-title>
                <v-text-field v-model="searchEducationLanguage" :label="$t('Qidiruv')" clearable hide-details dense />
              </v-card-title>
              <v-divider />
              <v-card-text class="filter-card-body">
                <div class="filter-checks">
                  <v-checkbox
                    v-for="item in filteredEducationLanguage" :key="item.id"
                    :label="item.name" :value="item.id"
                    v-model="selectedEducationLanguage" hide-details dense
                    class="mr-4 mb-2" style="min-width: 120px;"
                    @change="applyFilters"
                  />
                </div>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn text @click="educationLanguageMenu = false">{{ $t("Close") }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>

          <!-- Gender -->
          <v-menu v-model="genderMenu" :close-on-content-click="false" offset-y transition="scale-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="button-primary" v-bind="attrs" v-on="on">
                {{ getFilterLabel(selectedGender, gender, $t("Jinsi")) }}
              </v-btn>
            </template>
            <v-card class="filter-card" :style="{ backgroundColor: $vuetify.theme.dark ? '#1a1b23' : '#FFF' }">
              <v-card-title>
                <v-text-field v-model="searchGender" :label="$t('Qidiruv')" clearable hide-details dense />
              </v-card-title>
              <v-divider />
              <v-card-text class="filter-card-body">
                <div class="filter-checks">
                  <v-checkbox
                    v-for="item in filteredGender" :key="item.id"
                    :label="item.name" :value="item.id"
                    v-model="selectedGender" hide-details dense
                    class="mr-4 mb-2" style="min-width: 120px;"
                    @change="applyFilters"
                  />
                </div>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn text @click="genderMenu = false">{{ $t("Close") }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>

          <!-- Type Abiturient -->
          <v-menu v-model="typeAbiturientMenu" :close-on-content-click="false" offset-y transition="scale-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="button-primary" v-bind="attrs" v-on="on">
                {{ getFilterLabel(selectedTypeAbiturient, typeAbiturient, $t("Abiturient turi")) }}
              </v-btn>
            </template>
            <v-card class="filter-card" :style="{ backgroundColor: $vuetify.theme.dark ? '#1a1b23' : '#FFF' }">
              <v-card-title>
                <v-text-field v-model="searchTypeAbiturient" :label="$t('Qidiruv')" clearable hide-details dense />
              </v-card-title>
              <v-divider />
              <v-card-text class="filter-card-body">
                <div class="filter-checks">
                  <v-checkbox
                    v-for="item in filteredTypeAbiturient" :key="item.id"
                    :label="item.name" :value="item.id"
                    v-model="selectedTypeAbiturient" hide-details dense
                    class="mr-4 mb-2" style="min-width: 120px;"
                    @change="applyFilters"
                  />
                </div>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn text @click="typeAbiturientMenu = false">{{ $t("Close") }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>

          <!-- Sana (contract2026.created_date range) — toggle chip reveals the inline custom DatePickers -->
          <div class="date-filter-group">
            <v-btn small class="button-primary sana-toggle" :class="{ 'date-preset--active': dateFilterActive }"
                   @click="showDateControls = !showDateControls">
              <svg class="time-fast-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="m9 24h-8a1 1 0 0 1 0-2h8a1 1 0 0 1 0 2z"/>
                <path d="m7 20h-6a1 1 0 0 1 0-2h6a1 1 0 0 1 0 2z"/>
                <path d="m5 16h-4a1 1 0 0 1 0-2h4a1 1 0 0 1 0 2z"/>
                <path d="m13 23.955a1 1 0 0 1 -.089-2 10 10 0 1 0 -10.87-10.865 1 1 0 0 1 -1.992-.18 12 12 0 0 1 23.951 1.09 11.934 11.934 0 0 1 -10.91 11.951c-.03.003-.061.004-.09.004z"/>
                <path d="m12 6a1 1 0 0 0 -1 1v5a1 1 0 0 0 .293.707l3 3a1 1 0 0 0 1.414-1.414l-2.707-2.707v-4.586a1 1 0 0 0 -1-1z"/>
              </svg>
              {{ $t("Sana") }}
            </v-btn>
            <template v-if="showDateControls || dateFilterActive">
              <v-btn small class="button-primary" :class="{ 'date-preset--active': isDatePreset('today') }"
                     @click="setDatePreset('today')">{{ $t("Bugungi") }}</v-btn>
              <v-btn small class="button-primary" :class="{ 'date-preset--active': isDatePreset('yesterday') }"
                     @click="setDatePreset('yesterday')">{{ $t("Kechagi") }}</v-btn>
              <DatePicker
                :value="selectedDateFrom || null" @input="onDateFrom"
                :label="$t('Dan')" icon="mdi-calendar-start"
                :max="selectedDateTo || undefined" class="date-picker-field"
              />
              <DatePicker
                :value="selectedDateTo || null" @input="onDateTo"
                :label="$t('Gacha')" icon="mdi-calendar-end"
                :min="selectedDateFrom || undefined" class="date-picker-field"
              />
              <v-btn v-if="dateFilterActive" small text @click="clearDateFilter">
                <v-icon left small>mdi-close</v-icon>{{ $t("Tozalash") }}
              </v-btn>
            </template>
          </div>
        </div>
      </div>
    </v-fade-transition>

    <!-- ── Delete Confirmation Dialog ── -->
    <v-dialog v-model="isDeleteModal" max-width="440">
      <v-card style="border-radius: 14px;">
        <div class="delete-dialog-title">O'chirish</div>
        <!-- The message colour was inline #344054 — a dark slate that all but vanished on the dark
             card. It is a class now so dark mode can override it. -->
        <div class="delete-dialog-text">{{ deleteMessage }}</div>
        <div class="delete-dialog-actions">
          <v-btn text class="create-cancel-btn" @click="isDeleteModal = false">Bekor qilish</v-btn>
          <v-btn depressed class="confirm-delete-btn" @click="confirmDelete">Ha, o'chirish</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ── Table ── -->
    <v-data-table
      :mobile-breakpoint="0"
      :loading="isLoading"
      hide-default-footer
      hide-default-header
      :headers="tableHeaders"
      :items="items"
      :options.sync="options"
      :server-items-length="totalItemsCount"
      item-key="id"
      :items-per-page="itemsPerPage"
      :page.sync="page"
      class="elevation-0 app-table"
    >
      <template v-slot:header="{ props: headerProps }">
        <thead class="eq-thead">
          <tr>
            <th v-for="h in headerProps.headers" :key="h.value" :style="{ width: h.width ? h.width + 'px' : 'auto' }">
              <div
                v-if="h.value === 'checkbox'"
                class="cbox"
                :class="{ 'cbox--checked': allSelected, 'cbox--indeterminate': someSelected }"
                @click="toggleSelectAll"
              >
                <svg v-if="allSelected" viewBox="0 0 16 16" class="cbox__icon"><path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="#FFF" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
                <svg v-else-if="someSelected" viewBox="0 0 16 16" class="cbox__icon"><line x1="4" y1="8" x2="12" y2="8" stroke="#FFF" stroke-width="2.2" stroke-linecap="round"/></svg>
              </div>
              <template v-else>{{ h.text }}</template>
            </th>
          </tr>
        </thead>
      </template>

      <template v-slot:body="{ items: rows }">
        <tbody>
          <tr
            v-for="item in rows"
            :key="item.id"
            class="eq-row"
            :class="{ 'eq-row--selected': selectAllMode || selectedIds.includes(item.id) }"
            @click="onOpen(item)"
            style="cursor: pointer;"
          >
            <td class="eq-td-check" @click.stop>
              <div
                class="cbox"
                :class="{ 'cbox--checked': selectAllMode || selectedIds.includes(item.id) }"
                @click="toggleSelect(item.id)"
              >
                <svg v-if="selectAllMode || selectedIds.includes(item.id)" viewBox="0 0 16 16" class="cbox__icon"><path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="#FFF" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
              </div>
            </td>
            <td v-if="visibleColumnKeys.includes('fullName')" v-text="getFullName(item)" />
            <td v-if="visibleColumnKeys.includes('registrationDate')" v-text="$date(item.registrationDate)" />
            <td v-if="visibleColumnKeys.includes('modifiedDate')" v-text="$dateTime(item.modifiedDate)" />
            <td v-if="visibleColumnKeys.includes('faculty')" v-text="getName(item.faculty)" />
            <td v-if="visibleColumnKeys.includes('mobilePhone')" v-text="$tel(item.mobilePhone)" />
            <td v-if="visibleColumnKeys.includes('passportNumber')" v-text="item.passportNumber" />
            <td v-if="visibleColumnKeys.includes('passedDate')" v-text="$date(item.passedDate)" />
            <td v-if="visibleColumnKeys.includes('dtmScore')" v-text="item.dtmScore || '—'" />
            <td v-if="visibleColumnKeys.includes('dtmAbiturientNumber')" v-text="item.dtmAbiturientNumber || '—'" />
          </tr>
          <tr v-if="isLoading && !rows.length">
            <td :colspan="tableHeaders.length">
              <table-skeleton :cols="tableHeaders.length" />
            </td>
          </tr>
          <tr v-else-if="!rows.length">
            <td :colspan="tableHeaders.length">
              <empty-state :title="$t('No results found')" />
            </td>
          </tr>
        </tbody>
      </template>
    </v-data-table>

    <div class="pager-wrap">
      <v-pagination
        v-model="page"
        :length="paginationCount"
        :total-visible="7"
        color="#ffd451"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* ── Toolbar Row ── */
.toolbar-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 20px 12px;
}

.toolbar-row > .search-input {
  flex: 1;
  min-width: 0;
}

/* ── Circled Action Buttons ── */
.circle-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  max-width: 100%;
  flex-wrap: wrap;
  row-gap: 10px;
}

.circle-actions > * {
  flex-shrink: 0;
}

/* ── Result count badge (same style as the contract list) ── */
.badge-count {
  font-size: 12px !important;
  padding: 4px 10px !important;
  font-weight: 600 !important;
}
.badge-count.primary {
  color: white !important;
}
.theme--dark .badge-count.primary {
  color: #111318 !important;
}

/* ── Filter Button (plain HTML) ── */
.filter-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  width: 40px;
  height: 40px;
  min-width: 40px;
  max-width: 40px;
  padding: 0;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #d0d5dd;
  outline: none;
}

.filter-btn img {
  width: 18px;
  height: 18px;
  object-fit: cover;
}

.filter-btn:hover {
  background-color: #e8e8e8;
}

.filter-btn:active {
  background-color: #ddd;
  transform: scale(0.97);
}

.filter-btn--dark {
  background-color: #22232e;
  border-color: #33354a;
}

.filter-btn--dark:hover {
  background-color: #2a2c3a;
}

.filter-btn--dark:active {
  background-color: #33354a;
}

/* ── Circle Button Base (plain HTML) ── */
.circle-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  max-width: 40px;
  padding: 0;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  outline: none;
}

.circle-btn--columns {
  background-color: #f5f5f5;
  border: 1px solid #d0d5dd;
}

.circle-btn--columns:hover {
  background-color: #e8e8e8;
}

.circle-btn--columns-dark {
  background-color: #22232e;
  border-color: #33354a;
}

.circle-btn--columns-dark:hover {
  background-color: #2a2c3a;
}

.columns-icon {
  width: 13px;
  height: 13px;
  opacity: 0.6;
}

.columns-icon--dark {
  filter: brightness(0) invert(1);
  opacity: 0.7;
}

/* Excel export button */
.modern-export {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 10px !important;
  background: #217346 !important;
  color: #fff !important;
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600 !important;
  letter-spacing: .03em;
  text-transform: none !important;
  transition: all .2s ease;
  height: 36px !important;
  min-width: 130px !important;
}
.modern-export:hover {
  background: #1a5c38 !important;
}
.modern-export:active {
  transform: scale(.97);
}
.export-icon {
  width: 18px;
  height: 18px;
  filter: brightness(0) invert(1);
  margin-right: 10px;
}

/* White re-send-to-Bitrix button (matches export size, white palette) */
.modern-resend {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 10px !important;
  background: #fff !important;
  color: #344054 !important;
  border: 1px solid #d0d5dd !important;
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600 !important;
  letter-spacing: .03em;
  text-transform: none !important;
  transition: all .2s ease;
  height: 36px !important;
}
.modern-resend:hover {
  background: #f9fafb !important;
  border-color: #c0c7d0 !important;
}
.modern-resend:active {
  transform: scale(.97);
}
.resend-icon {
  width: 18px;
  height: 18px;
  margin-right: 10px;
}

.circle-btn--add {
  background-color: #ffd451;
  border: 1px solid #f5c542;
}

.circle-btn--add:hover {
  background-color: #f5c542;
}

.circle-btn--add:active {
  background-color: #e6b432;
  transform: scale(0.97);
}

/* Square (rounded-rect) action button — same footprint as circle-btn but with
   a softer border-radius so upload-type actions read as distinct from the
   circular add/edit/accept/delete glyphs. */
.square-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  max-width: 40px;
  padding: 0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  outline: none;
}

.square-btn--upload {
  background-color: #ffd451;
  border: 1px solid #f5c542;
}
/* Excel green — the button opens an .xlsx import, so it reads as a spreadsheet action rather than
   another yellow toolbar button. */
.square-btn--dtm {
  background-color: #217346;
  border: 1px solid #1a5c38;
}
.square-btn--dtm:hover {
  background-color: #1a5c38;
}

.dtm-progress {
  background: #f8fafc;
  border: 1px solid rgba(0, 30, 60, .08);
  border-radius: 10px;
  padding: 12px;
}
.dtm-phase { font-weight: 700; font-size: 12.5px; color: #217346; letter-spacing: .3px; }
.dtm-counters { display: flex; gap: 6px; flex-wrap: wrap; }
.dtm-chip {
  font-size: 12px; padding: 2px 8px; border-radius: 999px;
  background: #e2e8f0; color: #334155;
}
.dtm-chip--ok { background: #dcfce7; color: #166534; }
.dtm-chip--err { background: #fee2e2; color: #991b1b; }
/* Row errors are the point of the report, so the list scrolls instead of pushing the start button
   off the dialog when a file has many bad rows. */
.dtm-errors { max-height: 160px; overflow-y: auto; font-size: 12px; line-height: 1.5; }
.dtm-error-row { padding: 3px 0; border-bottom: 1px dashed rgba(0, 30, 60, .08); color: #475569; }
/* Warnings, not failures — amber so they are not mistaken for the red error list above. */
.dtm-resolved { border-left: 3px solid #f59e0b; padding-left: 8px; }
.dtm-resolved-title { font-weight: 700; font-size: 12px; color: #b45309; margin-bottom: 4px; }
.theme--dark .dtm-resolved-title { color: #fbbf24; }
.dtm-start-btn {
  background-color: #217346 !important;
  color: #fff !important;
  font-weight: 600;
}
/* Vuetify's disabled styling wins over the green above, so the button reads as plain text with no
   surface at all until a file is picked. Give the disabled state its own muted surface instead. */
.dtm-start-btn.v-btn--disabled {
  background-color: rgba(33, 115, 70, .28) !important;
  color: rgba(255, 255, 255, .6) !important;
}

/* ── Dark mode ──
   The progress panel was a light slate card (#f8fafc) sitting inside a dark dialog — the one thing
   in the whole modal still lit up. Repaint the surfaces; the green accent works on both themes and
   is deliberately kept so the panel still reads as the same feature as the import button. */
.theme--dark .dtm-progress {
  background: rgba(255, 255, 255, .04);
  border-color: rgba(255, 255, 255, .10);
}
.theme--dark .dtm-phase {
  color: #4ade80;   /* #217346 is unreadable on a dark surface */
}
.theme--dark .dtm-chip {
  background: rgba(255, 255, 255, .08);
  color: #cbd5e1;
}
.theme--dark .dtm-chip--ok {
  background: rgba(74, 222, 128, .16);
  color: #86efac;
}
.theme--dark .dtm-chip--err {
  background: rgba(248, 113, 113, .16);
  color: #fca5a5;
}
.theme--dark .dtm-error-row {
  border-bottom-color: rgba(255, 255, 255, .08);
  color: #cbd5e1;
}

.square-btn--upload:hover {
  background-color: #f5c542;
}

.square-btn--upload:active {
  background-color: #e6b432;
  transform: scale(0.97);
}

.circle-btn--edit {
  background-color: #7c3aed;
  border: 1px solid #6d28d9;
}

.circle-btn--edit:hover {
  background-color: #6d28d9;
}

.circle-btn--edit:active {
  background-color: #5b21b6;
  transform: scale(0.97);
}

.circle-btn--delete {
  position: relative;
  background-color: #dc2626;
  border: 1px solid #b91c1c;
}

.circle-btn--delete:hover {
  background-color: #b91c1c;
}

.circle-btn--delete:active {
  background-color: #991b1b;
  transform: scale(0.97);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.spin-icon {
  animation: spin 1s linear infinite;
}

.delete-badge {
  position: absolute;
  top: -12px;
  right: -3px;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  border-radius: 7px;
  background: #dc2626;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  line-height: 14px;
  text-align: center;
}

/* ── Filter Bar ── */
.filter-bar {
  padding: 12px 16px;
  border-bottom: 1px solid #e6e8ec;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.button-primary {
  background-color: #ffffff;
  border: 1px solid #d0d5dd;
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: 10px;
  transition: all 0.15s ease-in-out;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  cursor: pointer;
  text-transform: none;
}

.button-primary:hover {
  background-color: #f9fafb;
  border-color: #c0c7d0;
}

.button-primary:active {
  background-color: #f0f2f5;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
}

.button-primary--selected {
  background-color: #2e90fa;
  border-color: #2e90fa;
  color: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.filter-card {
  max-width: 300px;
  max-height: 400px;
}

.filter-card-body {
  max-height: 260px;
  overflow-y: auto;
}

.filter-checks {
  display: flex;
  flex-wrap: wrap;
}

/* ── Date range filter (inline custom DatePicker, same component as /payments) ── */
.date-filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.date-filter-group .date-picker-field {
  width: 168px;
}
.sana-toggle {
  display: inline-flex;
  align-items: center;
}
.time-fast-icon {
  width: 16px;
  height: 16px;
  margin-right: 6px;
  flex-shrink: 0;
  vertical-align: middle;
}
.date-preset--active {
  outline: 2px solid #00274c;
  outline-offset: 1px;
}
.theme--dark .date-preset--active {
  outline-color: #ffd451;
}

/* ── Search Input ── */
.search-input {
  max-width: none;
}

::v-deep .search-input {
  .v-input__slot {
    background: #f1f5f9 !important;
    border-radius: 10px !important;
    min-height: 38px !important;
  }
  fieldset {
    border-color: transparent !important;
  }
  &:hover fieldset {
    border-color: #d0d5dd !important;
  }
  &.v-input--is-focused fieldset {
    border-color: #00274c !important;
    border-width: 1.5px !important;
  }
  .v-icon {
    color: #94a3b8 !important;
    font-size: 20px !important;
  }
  input {
    font-size: 14px;
  }
  input::placeholder {
    color: #94a3b8 !important;
  }
}

.confirm-delete-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  background: #dc2626 !important;
  color: #fff !important;
  border-radius: 10px !important;
  padding: 0 20px !important;
  height: 40px !important;
}

.confirm-delete-btn:hover {
  background: #b91c1c !important;
}

/* ── Delete confirmation dialog ──
   Selectors are qualified with .v-card so they outrank Vuetify's own themed button rules whatever
   order the stylesheets land in — the destructive action losing its red and rendering as bare text
   is the failure mode here, and it reads as the *secondary* choice when that happens. */
.delete-dialog-title {
  padding: 20px 24px 12px;
  font-size: 16px;
  font-weight: 700;
}
.delete-dialog-text {
  padding: 0 24px 20px;
  font-size: 14px;
  color: #344054;
}
.delete-dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 24px 20px;
}
.v-card .confirm-delete-btn {
  background: #dc2626 !important;
  color: #fff !important;
}
.v-card .confirm-delete-btn:hover {
  background: #b91c1c !important;
}
/* Give cancel a real surface too, so the two are told apart by colour rather than by which one
   happens to hold focus — on open the focus ring made CANCEL look like the primary button. */
.v-card .create-cancel-btn {
  background: #f1f5f9 !important;
  color: #475569 !important;
}

.theme--dark .delete-dialog-text {
  color: #cbd5e1;
}
.theme--dark .v-card .create-cancel-btn {
  background: rgba(255, 255, 255, .08) !important;
  color: #e2e8f0 !important;
}
.theme--dark .v-card .create-cancel-btn:hover {
  background: rgba(255, 255, 255, .14) !important;
  color: #fff !important;
}
/* Red works on both themes; only lift it slightly so it does not go muddy on a dark surface. */
.theme--dark .v-card .confirm-delete-btn {
  background: #ef4444 !important;
}
.theme--dark .v-card .confirm-delete-btn:hover {
  background: #dc2626 !important;
}

/* ── Table ── */
.eq-thead th {
  font-size: 11px !important;
  font-weight: 800 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.06em !important;
  color: #667085 !important;
  background: #f9fafb !important;
  border-bottom: 1px solid #e6e8ec !important;
  padding: 10px 16px !important;
  vertical-align: middle;
}

.eq-thead th:first-child {
  width: 50px !important;
  min-width: 50px !important;
  max-width: 50px !important;
  padding: 4px 0 4px 12px !important;
}

.eq-td-check {
  width: 50px !important;
  min-width: 50px !important;
  max-width: 50px !important;
  padding: 4px 0 4px 12px !important;
  vertical-align: middle;
}

.eq-row--selected {
  background: #fffbeb !important;
}

/* ── Custom Checkbox ── */
.cbox {
  width: 20px;
  height: 20px;
  border: 2px solid #d0d5dd;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  background: #fff;
  flex-shrink: 0;
}

.cbox:hover {
  border-color: #00274d;
}

.cbox--checked,
.cbox--indeterminate {
  background: #00274d !important;
  border-color: #00274d !important;
}

.cbox__icon {
  width: 16px;
  height: 16px;
}

.theme--dark .cbox {
  background: #22232e;
  border-color: #44466a;
}

.theme--dark .cbox:hover {
  border-color: #ffd451;
}

.theme--dark .cbox--checked,
.theme--dark .cbox--indeterminate {
  background: #ffd451 !important;
  border-color: #ffd451 !important;
}

.theme--dark .cbox--checked .cbox__icon path,
.theme--dark .cbox--checked .cbox__icon line,
.theme--dark .cbox--indeterminate .cbox__icon path,
.theme--dark .cbox--indeterminate .cbox__icon line {
  stroke: #111318 !important;
}

/* ── Columns Menu ── */
.columns-card {
  min-width: 220px;
  border-radius: 12px !important;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12) !important;
}

.columns-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
}

.columns-title {
  font-size: 14px;
  font-weight: 700;
  color: #344054;
}

.columns-body {
  padding: 8px 16px 12px;
  max-height: 300px;
  overflow-y: auto;
}

.columns-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  cursor: pointer;
  border-radius: 6px;
}

.columns-item:hover {
  background: #f9fafb;
}

.columns-label {
  font-size: 14px;
  color: #344054;
}

.theme--dark .columns-item:hover {
  background: #22232e;
}

.theme--dark .columns-label {
  color: #d5d6de;
}

/* ── Pagination ── */
.pager-wrap {
  display: flex;
  justify-content: center;
  padding: 16px 20px;
  border-top: 1px solid #f1f3f5;
}

.pager-wrap::v-deep .v-pagination__item {
  border: 1px solid #d0d5dd;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  font-weight: 500;
  font-size: 14px;
  border-radius: 8px;
}

.pager-wrap::v-deep .v-pagination__item--active {
  background: #ffd451 !important;
  border-color: #f5c542 !important;
  color: #1a1500 !important;
}

.pager-wrap::v-deep .v-pagination__navigation {
  border: 1px solid #d0d5dd;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  border-radius: 8px;
}

/* ── Create Student Dialog ── */
.create-card {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(0,0,0,.12), 0 2px 8px rgba(0,0,0,.06) !important;
}

.create-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 28px 20px;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
  border-bottom: 1px solid #eef0f4;
}

.create-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.create-header-icon {
  background: linear-gradient(135deg, #ffd451, #f5c542);
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(255,212,81,.3);
}
.create-header-svg { display: block; }

.create-close-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #94a3b8;
  transition: all .15s;
}
.create-close-btn:hover {
  background: #f1f5f9;
  color: #475569;
}

.create-title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.create-subtitle {
  font-size: 13px;
  color: #94a3b8;
  margin-top: 3px;
}

.create-body {
  padding: 8px 28px 28px !important;
}

/* ── Sections ── */
.create-section {
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid #f1f3f7;
}
.create-section--last {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.create-section-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  margin-top: 20px;
}

.create-section-icon {
  color: #94a3b8 !important;
}

.create-section-label {
  font-size: 12px;
  font-weight: 700;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 0;
}

/* ── Grid layouts ── */
.create-grid {
  display: grid;
  gap: 14px;
  margin-bottom: 14px;
}
.create-grid:last-child {
  margin-bottom: 0;
}
.create-grid--3 {
  grid-template-columns: repeat(3, 1fr);
}
.create-grid--2 {
  grid-template-columns: repeat(2, 1fr);
}
@media (max-width: 680px) {
  .create-grid--3,
  .create-grid--2 {
    grid-template-columns: 1fr;
  }
}

::v-deep .create-field {
  /* RESTING: dual-coded — subtle tonal fill + a real, visible slate border,
     so each field reads as a distinct surface on the white card. */
  .v-input__slot {
    border-radius: 10px !important;
    min-height: 44px !important;
    background: #f6f9fc !important;
    transition: background-color .15s ease, box-shadow .15s ease;
  }
  fieldset {
    border-color: #8492a8 !important;      /* ~3.2:1 on white — visible on its own */
    border-width: 1px !important;
    transition: border-color .15s ease, border-width .15s ease;
  }
  /* HOVER: firm up edge + deepen fill */
  &:hover .v-input__slot { background: #eef3f9 !important; }
  &:hover fieldset { border-color: #5b6b85 !important; }
  /* FOCUS: field activates to pure white, navy edge + soft ring */
  &.v-input--is-focused .v-input__slot {
    background: #ffffff !important;
    box-shadow: 0 0 0 3px rgba(0, 39, 76, .10);
  }
  &.v-input--is-focused fieldset {
    border-color: #00274c !important;
    border-width: 1.5px !important;
  }
  &.v-input--is-focused label { color: #00274c !important; }
  /* TEXT ROLES — readable at rest, never ghosted */
  label { font-size: 13px; color: #475569; }
  input, .v-select__selection { font-size: 14px; font-weight: 500; color: #0f172a; }
  input::placeholder, textarea::placeholder { color: #64748b; opacity: 1; }
}

.create-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 28px;
  background: #fafbfc;
  border-top: 1px solid #eef0f4;
}

.create-cancel-btn {
  text-transform: none !important;
  font-weight: 500 !important;
  font-size: 14px !important;
  color: #64748b !important;
  border-radius: 10px !important;
  letter-spacing: 0 !important;
}
.create-cancel-btn:hover {
  color: #334155 !important;
}

.create-save-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  background: #ffd451 !important;
  color: #1a1500 !important;
  border-radius: 10px !important;
  padding: 0 22px !important;
  height: 42px !important;
  border: none !important;
  box-shadow: none !important;
}

.create-save-btn:hover {
  background: #f5c542 !important;
}

/* ══════════════════════════════════════════════════════════════
   Dark Mode (scoped)
   ══════════════════════════════════════════════════════════════ */
/* ── Filter icon dark ── */
.filter-icon-dark {
  filter: brightness(0) invert(1);
  opacity: 0.7;
}

.theme--dark .filter-bar {
  border-bottom-color: #2a2c3a;
}

.button-primary.theme--dark {
  background-color: #22232e !important;
  color: #d5d6de !important;
  border-color: #33354a !important;
}
.button-primary.theme--dark:hover {
  background-color: #2a2c3a !important;
}


.theme--dark .columns-title {
  color: #d5d6de !important;
}


.theme--dark .eq-thead th {
  background: #1a1b23 !important;
  color: #8b8d9e !important;
  border-bottom-color: #2a2c3a !important;
}

.theme--dark .eq-row--selected {
  background: #2a2520 !important;
}


/* Dark: Vuetify applies .theme--dark directly ON the v-input element,
   so we target .search-input.theme--dark (same element) */
::v-deep .search-input.theme--dark {
  .v-input__slot {
    background: #22232e !important;
  }
  fieldset {
    border-color: transparent !important;
  }
  &:hover fieldset {
    border-color: #33354a !important;
  }
  &.v-input--is-focused fieldset {
    border-color: #ffd451 !important;
  }
  .v-icon {
    color: #606275 !important;
  }
  input {
    color: #d5d6de !important;
  }
  input::placeholder {
    color: #606275 !important;
  }
}


.theme--dark .pager-wrap {
  border-top-color: #2a2c3a;
}

.theme--dark::v-deep .v-pagination__item {
  background: #22232e !important;
  color: #d5d6de !important;
  border-color: #33354a !important;
}

.theme--dark::v-deep .v-pagination__navigation {
  background: #22232e !important;
  border-color: #33354a !important;
}

.theme--dark .pager-wrap {
  border-top-color: #2a2c3a;
}

/* Dark: Create Dialog */
.theme--dark .create-card {
  background: #1a1b23 !important;
  box-shadow: 0 24px 80px rgba(0,0,0,.5), 0 2px 8px rgba(0,0,0,.3) !important;
}

.theme--dark .create-header {
  background: linear-gradient(180deg, #1e1f28 0%, #1a1b23 100%);
  border-bottom-color: #2a2c3a;
}

.theme--dark .create-close-btn {
  color: #6b7280;
}
.theme--dark .create-close-btn:hover {
  background: #22232e;
  color: #d5d6de;
}

.theme--dark .create-header-icon {
  background: linear-gradient(135deg, #ffd451, #f5c542);
  box-shadow: 0 2px 8px rgba(255,212,81,.15);
}

.theme--dark .create-title {
  color: #e4e5eb;
}

.theme--dark .create-subtitle {
  color: #6b7280;
}

.theme--dark .create-section {
  border-bottom-color: #22232e;
}

.theme--dark .create-section-icon {
  color: #6b7280 !important;
}

.theme--dark .create-section-label {
  color: #6b7280;
}

::v-deep .theme--dark .create-field {
  /* RESTING: fill sits ABOVE the dark card so the field reads as a raised
     surface, supported by a clearly visible border. */
  .v-input__slot {
    background: #2a2c38 !important;
    transition: background-color .15s ease, box-shadow .15s ease;
  }
  fieldset {
    border-color: #565a76 !important;
    border-width: 1px !important;
  }
  &:hover .v-input__slot { background: #30323f !important; }
  &:hover fieldset { border-color: #6f7493 !important; }
  /* FOCUS: surface brightens, gold edge + ring */
  &.v-input--is-focused .v-input__slot {
    background: #32343f !important;
    box-shadow: 0 0 0 3px rgba(255, 212, 81, .14);
  }
  &.v-input--is-focused fieldset {
    border-color: #ffd451 !important;
    border-width: 1.5px !important;
  }
  &.v-input--is-focused label { color: #ffd451 !important; }
  label { color: #a3a8bd; }
  input, .v-select__selection { color: #e8e9f0; }
  input::placeholder, textarea::placeholder { color: #969cb1; opacity: 1; }
}

.theme--dark .create-footer {
  background: #16171e;
  border-top-color: #2a2c3a;
}

.theme--dark .create-cancel-btn {
  color: #8b8d9e !important;
}
.theme--dark .create-cancel-btn:hover {
  color: #d5d6de !important;
}

.theme--dark .create-save-btn {
  background: #ffd451 !important;
  color: #111318 !important;
  box-shadow: none !important;
}

.theme--dark .create-save-btn:hover {
  background: #f5c542 !important;
}
@media (max-width: 600px) {
  .create-header { padding: 16px 16px 14px; }
  .create-body { padding: 8px 16px 20px !important; }
  .create-footer { padding: 12px 16px; }
}
</style>

<!-- Global (unscoped) on purpose: the "Manba" dropdown menu is teleported to the
     app root, outside this component's scoped styles, so it can't be reached with
     ::v-deep. We pin the menu content as the single scroll container (hard
     max-height + overflow-y:auto) and strip any height/clip from the inner list.
     This also makes Vuetify's hasScrollbar() true, so the v-dialog wheel
     scroll-lock lets the option list scroll instead of swallowing the event. -->
<style lang="scss">
.v-menu__content.upload-source-menu {
  max-height: 240px !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
  -webkit-overflow-scrolling: touch;
}
.v-menu__content.upload-source-menu .v-select-list,
.v-menu__content.upload-source-menu .v-list {
  max-height: none !important;
  overflow: visible !important;
}
</style>
